import { useEffect, useRef, useState } from "react";
import { MoveJobLink } from "@/components/move-job-customer";
import { Link, useNavigate } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { api } from "@/lib/api";
import { openExternalLink } from "@/lib/standalone";
import { formatDate, formatDateOnly, formatDateTime } from "@/lib/utils";
import { classifyDueState, scheduledPastDueTitle } from "@/lib/dueUrgency";
import { isUmbrellaContactName, UMBRELLA_CONTACT_LABEL } from "@/lib/vocabulary";
import {
    Calendar,
    CalendarPlus,
    CheckCircle2,
    DollarSign,
    Droplet,
    ExternalLink,
    FileEdit,
    FileText,
    Loader2,
    Lock,
    Mail,
    Pencil,
    Phone,
    Plus,
    Sparkles,
    Tag,
    UserCheck,
    Users,
    X,
} from "lucide-react";
import { EmptyRow } from "./empty-row";
import { WaterReportingEditorDialog } from "@/components/water-reporting-editor";
import { ReportInputFlag } from "@/components/report-input-flag";
import { openPriceCheck } from "@/components/price-check";
import { useAuth } from "@/lib/auth";
import { PERMISSIONS } from "@/lib/permissions";
import {
    EARNING_MODE_LABEL,
    HELPER_VALUE_BADGE_CLS,
    HELPER_VALUE_LABEL,
    SCHEDULING_FLEXIBILITY_LABEL,
    techLabel,
    type AddCrewPayload,
    type HelperValueTier,
    type JobAppointment,
    type JobDetail,
    type TechOption,
    type WorkOrderMappingInfo,
} from "./types";

/** Convert an ISO timestamp to the `value` a `<input type="datetime-local">`
 *  expects (local wall-clock `YYYY-MM-DDTHH:mm`, no timezone suffix). */
function toDateTimeLocal(iso?: string | null): string {
    if (!iso) return "";
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return "";
    const pad = (n: number) => String(n).padStart(2, "0");
    return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

/** How an ended appointment reads in the visit timeline (Issue #454). "returned"
 *  now reads "Didn't complete" — the visit happened but the work wasn't finished. */
const APPOINTMENT_OUTCOME_LABEL: Record<string, string> = {
    rescheduled: "Rescheduled",
    returned: "Didn't complete",
    cannot_complete: "Couldn't complete",
    cancelled: "Cancelled",
    completed: "Completed",
};

/** Badge tone per outcome in the visit timeline. Amber = the visit happened but
 *  didn't finish; slate = a move/cancel that never ran; red = a hard stop. */
const APPOINTMENT_OUTCOME_TONE: Record<string, string> = {
    returned: "border-amber-500/40 bg-amber-500/10 text-amber-700 dark:text-amber-400",
    cannot_complete: "border-red-500/40 bg-red-500/10 text-red-700 dark:text-red-400",
    completed: "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    rescheduled: "border-border text-muted-foreground",
    cancelled: "border-border text-muted-foreground",
};

/** Office's quick "why didn't the last visit finish?" reasons. Keep the codes in
 *  sync with ADD_APPOINTMENT_REASONS on the server (src/server/routes/jobs.ts). */
const ADD_APPOINTMENT_REASONS: Array<{ code: string; label: string }> = [
    { code: "no_access", label: "Locked / no access" },
    { code: "nobody_home", label: "Nobody on site" },
    { code: "customer_not_ready", label: "Customer not ready" },
    { code: "ran_out_of_time", label: "Ran out of time" },
    { code: "parts_needed", label: "Parts needed" },
    { code: "weather", label: "Weather" },
    { code: "other", label: "Other" },
];

/** Compact "Jul 23, 8:15 AM – 4:15 PM" window for the Visit history list. Uses
 *  the card's shared formatDateTime for the start; a time-only end when same-day. */
function formatVisitWindow(startIso: string, endIso: string): string {
    const start = new Date(startIso);
    const end = new Date(endIso);
    if (Number.isNaN(start.getTime())) return "—";
    const startStr = formatDateTime(startIso);
    if (Number.isNaN(end.getTime())) return startStr;
    const sameDay = start.toDateString() === end.toDateString();
    const endStr = sameDay
        ? end.toLocaleTimeString(undefined, { hour: "numeric", minute: "2-digit" })
        : formatDateTime(endIso);
    return `${startStr} – ${endStr}`;
}

/** Structural slice of a React Query mutation — the page owns the real
 *  mutations; the rail only needs mutate + isPending. */
interface MutationLike<TVars> {
    mutate: (vars: TVars, options?: { onSuccess?: () => void; onError?: (e: unknown) => void }) => void;
    isPending: boolean;
}

export interface RailMutations {
    updateAssignee: MutationLike<string | null>;
    addHelper: MutationLike<AddCrewPayload>;
    removeHelper: MutationLike<string>;
    updateJobFields: MutationLike<Record<string, unknown>>;
    updateSchedule: MutationLike<{ scheduledStart: string | null; scheduledEnd: string | null; force?: boolean }>;
    toggleConfirm: MutationLike<boolean>;
    updateHelperValue: MutationLike<HelperValueTier | null>;
}

// ── Customer & Site ─────────────────────────────────────────────────────────

// Same read surface as the Approve & Send modal (crm.ts contact-methods).
export interface ContactMethodRow {
    id: string;
    kind: string;
    value: string;
    label: string | null;
    memo: string | null;
    isPrimary: boolean;
}

export interface ContactWithMethods {
    id: string;
    firstName: string | null;
    lastName: string | null;
    roleTitle: string | null;
    siteId: string | null;
    isPrimary: boolean;
    methods: ContactMethodRow[];
}

export interface RankedMethod extends ContactMethodRow {
    contactName: string;
    /** #824: the owning contact is account-level (no site of its own) while
     *  THIS job is tied to a site — so the person may be in a head office,
     *  not at the door. Only meaningful when the job has a site. */
    accountLevel: boolean;
}

export function isPhoneKind(kind: string): boolean {
    return /phone|mobile|cell/i.test(kind);
}

/** Flatten contacts → methods ranked site-scoped first, then primary contact,
 *  then primary method — so "the number to call for THIS job" comes out on top. */
export function rankMethods(contacts: ContactWithMethods[], siteId: string | null): RankedMethod[] {
    const scored: Array<{ score: number; m: RankedMethod }> = [];
    contacts.forEach((c, ci) => {
        const name = [c.firstName, c.lastName].filter(Boolean).join(" ").trim();
        c.methods.forEach((m, mi) => {
            let score = ci * 100 + mi;
            if (c.isPrimary) score -= 10_000;
            if (m.isPrimary) score -= 1_000;
            if (siteId && c.siteId === siteId) score -= 100_000;
            scored.push({ score, m: { ...m, contactName: name, accountLevel: !!siteId && !c.siteId } });
        });
    });
    return scored.sort((a, b) => a.score - b.score).map((s) => s.m);
}

function ContactMethodLine({ method }: { method: RankedMethod }) {
    const phone = isPhoneKind(method.kind);
    const email = /email/i.test(method.kind);
    // #760: ST-imported rows used to carry the memo in BOTH label and memo,
    // and the contact's name is derived from that same memo — rendering
    // "Jason Gosnell · Jason Gosnell". Suppress the annotation when it just
    // repeats the name (covers pre-fix rows without any data migration).
    const annotation = method.label ?? method.memo;
    const dup = annotation && annotation.trim().toLowerCase() === method.contactName.trim().toLowerCase();
    // #824: the ST-import umbrella row ("ServiceTitan contacts") is a bucket
    // of numbers, not a person — never let it read as a name.
    const umbrella = isUmbrellaContactName(method.contactName);
    const note = [umbrella ? null : method.contactName, dup ? null : annotation].filter(Boolean).join(" · ");
    const inner = (
        <>
            {phone ? (
                <Phone className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
            ) : email ? (
                <Mail className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
            ) : (
                <Users className="h-3.5 w-3.5 shrink-0 text-foreground/70" />
            )}
            <span className="truncate font-semibold">{method.value}</span>
            {umbrella && (
                <span className="truncate text-[11px] font-semibold text-amber-600 dark:text-amber-400">
                    {UMBRELLA_CONTACT_LABEL}
                </span>
            )}
            {note && <span className="truncate text-[11px] font-normal text-foreground/60">{note}</span>}
            {method.accountLevel && (
                <span
                    className="shrink-0 rounded border border-amber-400/50 bg-amber-500/10 px-1 text-[9px] font-semibold text-amber-600 dark:text-amber-400"
                    title="This contact belongs to the customer account, not this site — they may not be at the door"
                >
                    account — not on-site
                </span>
            )}
        </>
    );
    if (phone) {
        return (
            <a
                href={`tel:${method.value.replace(/[^\d+]/g, "")}`}
                className="flex items-center gap-1.5 text-[13px] hover:text-primary"
            >
                {inner}
            </a>
        );
    }
    if (email) {
        return (
            <a href={`mailto:${method.value}`} className="flex items-center gap-1.5 text-[13px] hover:text-primary">
                {inner}
            </a>
        );
    }
    return <div className="flex items-center gap-1.5 text-[13px]">{inner}</div>;
}

function ContactRows({ customerId, siteId }: { customerId: string; siteId: string | null }) {
    const [expanded, setExpanded] = useState(false);
    const navigate = useNavigate();
    // #760: scoped to THIS job's site (account-level contacts + that site's).
    // A multi-location account's 300 other locations' contacts no longer pile
    // into one job's rail as an unexplained "304 more".
    const contactsQ = useQuery({
        queryKey: ["customer-contact-methods", customerId, siteId],
        queryFn: () =>
            api.get<{ contacts: ContactWithMethods[] }>(
                `/api/customers/${customerId}/contact-methods${siteId ? `?siteId=${siteId}` : ""}`,
            ),
    });

    if (contactsQ.isLoading) return null;
    const ranked = rankMethods(contactsQ.data?.contacts ?? [], siteId);
    if (ranked.length === 0) {
        return (
            <EmptyRow
                label="No contact on file"
                actionLabel="Add contact"
                onAction={() => navigate(`/customers/${customerId}`)}
            />
        );
    }
    // Primary phone + primary email up front; everything else behind "N more".
    const topPhone = ranked.find((m) => isPhoneKind(m.kind));
    const topEmail = ranked.find((m) => /email/i.test(m.kind));
    const top = [topPhone, topEmail].filter((m): m is RankedMethod => !!m);
    const rest = ranked.filter((m) => !top.some((t) => t.id === m.id));
    return (
        <div className="space-y-1">
            {(top.length > 0 ? top : ranked.slice(0, 2)).map((m) => (
                <ContactMethodLine key={m.id} method={m} />
            ))}
            {expanded && rest.map((m) => <ContactMethodLine key={m.id} method={m} />)}
            <div className="flex items-center gap-2 pt-0.5">
                {rest.length > 0 && !expanded && (
                    <button
                        type="button"
                        className="text-[11px] text-foreground/60 hover:text-foreground hover:underline"
                        onClick={() => setExpanded(true)}
                    >
                        Show all {rest.length + top.length} for this site
                    </button>
                )}
                {/* Always-available path to add/edit who to call — the "assign"
                    step the office said was buried. */}
                <button
                    type="button"
                    className="text-[11px] text-foreground/55 hover:text-primary hover:underline"
                    onClick={() => navigate(`/customers/${customerId}`)}
                    title="Add or edit this customer's contacts"
                >
                    Manage contacts
                </button>
            </div>
        </div>
    );
}

/** Left-aligned inline label so each line says WHAT it is — the office asked to
 *  drop the generic "Customer & Site" title and instead make it obvious what
 *  each row is (Customer / Site / Contact) and what clicking it opens. */
function RowLabel({ children }: { children: React.ReactNode }) {
    return (
        <span className="w-16 shrink-0 pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
            {children}
        </span>
    );
}

/** Identity typography (#769) — "tie this job to who, what, where and what
 *  type." The rail's identity values (customer, site, contact, visit, city,
 *  job type) are deliberately the loudest text on the page: ~14.5px bold
 *  under the small uppercase RowLabels, outranking body copy. */
const IDENTITY_VALUE_CLS = "text-[14.5px] font-bold leading-snug";

export function CustomerSiteCard({ job }: { job: JobDetail }) {
    const siteName = job.site_name ?? job.location_name ?? "—";
    const address = job.site_address_line ?? job.location_address;
    return (
        <Card data-testid="customer-site-card">
            <CardContent className="space-y-2.5 pt-4 text-sm">
                {/* Customer — the label says what it is; clicking opens the
                    customer record (title spells that out on hover). */}
                <div className="flex items-start gap-2">
                    <RowLabel>Customer</RowLabel>
                    {job.company_name ? (
                        <Link
                            to={`/customers/${job.customer_id}`}
                            title="Open the customer record"
                            className={`min-w-0 truncate text-primary hover:underline ${IDENTITY_VALUE_CLS}`}
                        >
                            {job.company_name}
                        </Link>
                    ) : (
                        <span className="text-foreground/70">No customer</span>
                    )}
                    {/* #1897 — office fix for a job built under the wrong customer. */}
                    <MoveJobLink jobId={job.id} className="ml-auto shrink-0" />
                </div>

                {/* Site — clicking opens the SITE record (distinct from the
                    customer), address underneath. */}
                <div className="flex items-start gap-2">
                    <RowLabel>Site</RowLabel>
                    <div className="min-w-0">
                        {job.site_id ? (
                            <Link
                                to={`/sites/${job.site_id}`}
                                title="Open the site record"
                                className={`text-primary hover:underline ${IDENTITY_VALUE_CLS}`}
                            >
                                {siteName}
                            </Link>
                        ) : (
                            <span className={IDENTITY_VALUE_CLS}>{siteName}</span>
                        )}
                        {address && (
                            <p className="text-xs text-foreground/70">
                                {address}
                                {job.site_city ? `, ${job.site_city}` : ""}
                            </p>
                        )}
                    </div>
                </div>

                {/* Contact — the site/location contact first (who to call for
                    THIS job), then the customer's ranked methods, then an
                    always-there path to add/edit contacts. #824 (audit C5):
                    the block adopts the site card's honesty — an ST-import
                    umbrella row never renders where a person's name goes, and
                    a site with no on-site contact says so in plain words
                    instead of borrowing a number without a caveat. */}
                <div className="flex items-start gap-2">
                    <RowLabel>Contact</RowLabel>
                    <div className="min-w-0 flex-1 space-y-1">
                        {job.site_contact_name && !isUmbrellaContactName(job.site_contact_name) ? (
                            <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px]">
                                <span className="font-bold">{job.site_contact_name}</span>
                                {(job.site_contact_phone || job.site_phone) && (
                                    <a
                                        href={`tel:${(job.site_contact_phone || job.site_phone || "").replace(/[^\d+]/g, "")}`}
                                        className="tabular-nums font-semibold hover:text-primary"
                                    >
                                        {job.site_contact_phone || job.site_phone}
                                    </a>
                                )}
                            </div>
                        ) : job.site_contact_name ? (
                            <div
                                data-testid="contact-umbrella"
                                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-[13px]"
                            >
                                <Badge
                                    variant="outline"
                                    className="border-amber-400/50 bg-amber-500/10 text-[10px] text-amber-600 dark:text-amber-400"
                                >
                                    {UMBRELLA_CONTACT_LABEL}
                                </Badge>
                                {(job.site_contact_phone || job.site_phone) && (
                                    <a
                                        href={`tel:${(job.site_contact_phone || job.site_phone || "").replace(/[^\d+]/g, "")}`}
                                        className="tabular-nums font-semibold hover:text-primary"
                                    >
                                        {job.site_contact_phone || job.site_phone}
                                    </a>
                                )}
                            </div>
                        ) : job.site_id ? (
                            <div
                                data-testid="contact-none"
                                className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs"
                            >
                                <span className="text-amber-600 dark:text-amber-400">
                                    No on-site contact is set for this location yet.
                                </span>
                                {job.site_phone && (
                                    <a
                                        href={`tel:${job.site_phone.replace(/[^\d+]/g, "")}`}
                                        className="tabular-nums font-semibold text-foreground hover:text-primary"
                                    >
                                        {job.site_phone}
                                        <span className="font-normal text-foreground/55"> (site line)</span>
                                    </a>
                                )}
                                <Link to={`/sites/${job.site_id}`} className="text-primary hover:underline">
                                    Set contact
                                </Link>
                            </div>
                        ) : null}
                        {job.customer_id && <ContactRows customerId={job.customer_id} siteId={job.site_id} />}
                    </div>
                </div>
            </CardContent>
        </Card>
    );
}

// ── Water & Reporting ───────────────────────────────────────────────────────

/**
 * WHO we report to and the account, as its own rail card (spec §6c.1) —
 * sitting between Customer & Site and Schedule & Crew. Data comes off the
 * job payload (site → purveyor → reporting platform joins in
 * jobDetailHeaderQuery); no extra fetch.
 *
 * Visuals mirror the field app's purveyor chip: an OPAQUE sky tint block
 * inside a standard white card. Never tint the Card itself — a translucent
 * bg on the Card replaces its opaque `bg-card` and lets the full-strength
 * BlueprintBackdrop watermark bleed through (washed out in light mode, a dim
 * overlay in dark). No `/NN` opacity suffixes on any background here.
 */
export function WaterReportingCard({ job }: { job: JobDetail }) {
    const [editing, setEditing] = useState(false);
    const { can } = useAuth();
    // ONE editor, every surface: the same Water & Reporting dialog the site
    // page and the customer Locations tab mount. Jurisdiction changes from
    // the job page land on the site, and the shared invalidation re-resolves
    // this job's reporting card, stepper, and §3f gating live.
    const editDialog =
        editing && job.site_id ? (
            <WaterReportingEditorDialog
                siteId={job.site_id}
                customerId={job.customer_id}
                onClose={() => setEditing(false)}
            />
        ) : null;
    if (!job.water_provider_name) {
        // Empty-state doctrine: backflow jobs surface the gap in one muted
        // row; other trades don't report to a purveyor at all.
        if (job.trade_code !== "backflow") return null;
        return (
            <Card className="border-sky-200/60 dark:border-sky-500/20">
                <CardContent className="pt-4 pb-4 flex items-center justify-between gap-2">
                    <EmptyRow
                        icon={<Droplet className="h-3.5 w-3.5 text-sky-600 dark:text-sky-400" />}
                        label="No water purveyor set"
                    />
                    {job.site_id && (
                        <Button variant="outline" size="sm" onClick={() => setEditing(true)}>
                            <Pencil className="mr-1.5 h-3.5 w-3.5" /> Set purveyor
                        </Button>
                    )}
                </CardContent>
                {editDialog}
            </Card>
        );
    }
    const platformBadge = job.water_provider_submission_platform
        ? `${job.water_provider_is_portal ? "Portal" : "Reports"}: ${job.water_provider_submission_platform}`
        : "Reports: Paper / manual";
    const method = job.water_provider_is_portal
        ? "Results entered in the purveyor's online portal"
        : job.water_provider_submission_platform
          ? `Reports filed via ${job.water_provider_submission_platform}`
          : "Reports filed on paper / mailed";
    return (
        <Card>
            <CardHeader className="pb-3">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="flex items-center gap-2">
                        <Droplet className="h-4 w-4 text-sky-600 dark:text-sky-400" /> Water &amp; Reporting
                    </span>
                    <span className="flex items-center gap-1">
                        {/* PRICE CHECK launched from a job pre-resolves the
                            site's pinned provider — no city typing, no
                            ambiguity question. Office only (pricing.view). */}
                        {job.water_provider_id && can(PERMISSIONS.pricing.view) && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs font-normal"
                                title="Price check — what a backflow test costs here"
                                onClick={() =>
                                    openPriceCheck({
                                        providerId: job.water_provider_id!,
                                        providerName:
                                            job.water_provider_display_name || job.water_provider_name || undefined,
                                    })
                                }
                            >
                                <DollarSign className="mr-1 h-3.5 w-3.5" /> Price check
                            </Button>
                        )}
                        {job.site_id && (
                            <Button
                                variant="ghost"
                                size="sm"
                                className="h-7 px-2 text-xs font-normal"
                                title="Edit jurisdiction, portal account, and on-site contact (saved on the site)"
                                onClick={() => setEditing(true)}
                            >
                                <Pencil className="mr-1 h-3.5 w-3.5" /> Edit
                            </Button>
                        )}
                    </span>
                </CardTitle>
            </CardHeader>
            <CardContent>
                <div className="space-y-2 rounded-md border border-l-4 border-l-sky-500 bg-sky-50 p-3 dark:border-l-sky-400 dark:bg-sky-950">
                    <div className="text-lg font-bold leading-tight text-foreground">
                        {job.water_provider_display_name || job.water_provider_name}
                    </div>
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1.5">
                        <Badge className="border-transparent bg-sky-700 text-white">{platformBadge}</Badge>
                        {job.water_provider_portal_url && (
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => openExternalLink(job.water_provider_portal_url!)}
                            >
                                <ExternalLink className="mr-1.5 h-3.5 w-3.5" />
                                Open portal
                            </Button>
                        )}
                    </div>
                    <PurveyorAccountRow job={job} onEdit={() => setEditing(true)} />
                    <p className="text-sm text-foreground">{method}</p>
                    <ReportInputFlag jobId={job.id} isPortal={job.water_provider_is_portal} />
                    {job.water_provider_report_name && (
                        <p className="text-sm text-foreground">Report form: {job.water_provider_report_name}</p>
                    )}
                </div>
            </CardContent>
            {editDialog}
        </Card>
    );
}

/**
 * Purveyor account number row (spec §6d.3). Stored on the SITE
 * (`sites.water_account_number` — the canonical portal account), never on
 * the job, so Regina enters it once per location and every future job there
 * — plus the City Filing Queue — shows it. The legacy ST location code
 * (`sites.account_number`) is NEVER shown as the portal account; when it's
 * all we have, it renders as a distinct muted reference line. Edits go
 * through the shared Water & Reporting editor dialog.
 */
function PurveyorAccountRow({ job, onEdit }: { job: JobDetail; onEdit: () => void }) {
    if (!job.site_id) return null;
    // A portal account only exists for portal jurisdictions. Paper/offline
    // cities (e.g. City of Hurst) file by mail — any number stored in
    // `water_account_number` there is a stale/legacy code that routes nowhere,
    // so never surface it. Mirrors the Water & Reporting editor, which offers
    // the account field for portals only ("reports offline — no account needed").
    if (!job.water_provider_is_portal) return null;
    const portalAccount = job.site_water_account_number?.trim() || null;
    const legacyCode = job.site_account_number?.trim() || null;

    if (!portalAccount) {
        return (
            <div className="space-y-1">
                <button
                    type="button"
                    className="text-sm font-medium text-sky-700 hover:underline dark:text-sky-300"
                    onClick={onEdit}
                >
                    + Add account #
                </button>
                {legacyCode && (
                    <p className="text-xs text-foreground/60">
                        Legacy ST code: <span className="font-mono">{legacyCode}</span> (not the portal account)
                    </p>
                )}
            </div>
        );
    }
    return (
        <div className="flex items-center gap-1.5 text-sm text-foreground">
            <span className="shrink-0">Account #:</span>
            <span className="font-mono font-semibold select-all">{portalAccount}</span>
            <button
                type="button"
                className="text-muted-foreground hover:text-primary"
                title="Edit the purveyor account number (saved on the site)"
                onClick={onEdit}
            >
                <Pencil className="h-3.5 w-3.5" />
            </button>
        </div>
    );
}

// ── Schedule & Crew ─────────────────────────────────────────────────────────

/**
 * Issue #1245 — the collapsed summary used to read "+ 1 helper" for ANY crew
 * member, so a second FULL TECH was announced as a helper. Count the roles.
 */
export function crewSummaryLabel(crew: Array<{ role: string }>): string {
    const techs = crew.filter((c) => c.role === "tech").length;
    const helpers = crew.length - techs;
    const parts: string[] = [];
    if (techs > 0) parts.push(`${techs} tech${techs === 1 ? "" : "s"}`);
    if (helpers > 0) parts.push(`${helpers} helper${helpers === 1 ? "" : "s"}`);
    return parts.join(" + ");
}

export function ScheduleCrewCard({
    job,
    postVisit,
    techs,
    techsLoading,
    m,
    editNonce,
}: {
    job: JobDetail;
    postVisit: boolean;
    techs: TechOption[];
    techsLoading: boolean;
    m: RailMutations;
    /** Increment to open the editor from outside (header "Schedule"). */
    editNonce?: number;
}) {
    const [editing, setEditing] = useState(false);
    const [schedStart, setSchedStart] = useState("");
    const [schedEnd, setSchedEnd] = useState("");
    const [helperInput, setHelperInput] = useState("");
    // Flexible crew: role + earning mode chosen at add-time. Mode defaults
    // follow the role (Tech=split evenly, Helper=mirror) until the office
    // overrides — Issue #1245: two techs on one job must not pay out twice.
    const [addRole, setAddRole] = useState<"tech" | "helper">("helper");
    const [addMode, setAddMode] = useState<"full" | "split_even" | "mirror">("mirror");
    const [modeTouched, setModeTouched] = useState(false);
    // CREW_FULL is never a dead end: the 409 becomes this inline prompt.
    const [crewFullPrompt, setCrewFullPrompt] = useState<{
        payload: AddCrewPayload;
        crewSize: number;
        raiseTo: number;
    } | null>(null);
    const [overrideReason, setOverrideReason] = useState("");
    const cardRef = useRef<HTMLDivElement | null>(null);
    const done = ["completed", "closed", "cancelled"].includes(job.status);
    // #769: the done-state collapse is a real collapse — the 2-line summary
    // expands on click to the full booking detail + visit history, and
    // EVERYTHING below (visit history included) stays inside it, so a
    // finished job's card genuinely reads as two lines until asked.
    const [expandedDone, setExpandedDone] = useState(false);
    const collapsed = done && !editing && !expandedDone;

    const openEditor = () => {
        setSchedStart(toDateTimeLocal(job.scheduled_start));
        setSchedEnd(toDateTimeLocal(job.scheduled_end));
        setEditing(true);
    };

    // Header "Schedule" on an unscheduled job lands here instead of
    // flipping the status chip (Prompt 21 3c) — open the editor and
    // bring the card into view.
    useEffect(() => {
        if (!editNonce) return;
        openEditor();
        cardRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [editNonce]);

    const confirmed = job.dispatch_lock_state === "confirmed";
    const crew = job.crew ?? [];

    // Issue #442: Visit history — every appointment this job has had, newest
    // first. The editor above schedules the NEXT visit; saving it inserts a new
    // appointment and the prior one is preserved here (marked superseded). The
    // key includes a placement signature so it refetches after a reschedule or a
    // crew change.
    const placementSig = `${job.scheduled_start ?? ""}|${job.scheduled_end ?? ""}|${job.assigned_to ?? ""}|${crew
        .map((c) => c.user_id)
        .join(",")}`;
    const appointmentsQuery = useQuery({
        queryKey: ["job-appointments", job.id, placementSig],
        queryFn: () => api.get<{ appointments: JobAppointment[] }>(`/api/jobs/${job.id}/appointments`),
    });
    const appointments = appointmentsQuery.data?.appointments ?? [];

    // Issue #454: declaring the whole job cannot-complete is an OFFICE decision
    // (a tech only ends the trip → "return needed"). Office-gated action here.
    const { can } = useAuth();
    const qc = useQueryClient();
    const [showCannotComplete, setShowCannotComplete] = useState(false);
    const [cannotCompleteReason, setCannotCompleteReason] = useState("");
    const cannotComplete = useMutation({
        mutationFn: (reason: string) => api.post(`/api/jobs/${job.id}/cannot-complete`, { reason }),
        onSuccess: () => {
            qc.invalidateQueries({ queryKey: ["job", job.id] });
            qc.invalidateQueries({ queryKey: ["job-appointments", job.id] });
            setShowCannotComplete(false);
            setCannotCompleteReason("");
            toast.success("Job marked cannot-complete");
        },
        onError: (e: unknown) => {
            const msg =
                (e as { body?: { message?: string; error?: string } })?.body?.message ??
                (e as { body?: { error?: string } })?.body?.error ??
                "unknown error";
            toast.error(`Couldn't mark cannot-complete: ${msg}`);
        },
    });
    const canOfficeCannotComplete =
        can(PERMISSIONS.jobs.manage) && !["completed", "closed", "cancelled", "cannot_complete"].includes(job.status);

    // ── Add another appointment (office-only) ──────────────────────────────
    // The current visit didn't finish → end it honestly ("Didn't complete") and
    // set up the next one. Default (no date) drops the job back on the schedule
    // board to be dragged onto a day; picking a date is the optional exception.
    const [addingAppt, setAddingAppt] = useState(false);
    const [apptReasonCode, setApptReasonCode] = useState<string | null>(null);
    const [apptNote, setApptNote] = useState("");
    const [apptPickDate, setApptPickDate] = useState(false);
    const [apptStart, setApptStart] = useState("");
    const [apptEnd, setApptEnd] = useState("");
    const resetApptForm = () => {
        setAddingAppt(false);
        setApptReasonCode(null);
        setApptNote("");
        setApptPickDate(false);
        setApptStart("");
        setApptEnd("");
    };
    const addAppointment = useMutation({
        mutationFn: (body: { scheduledStart?: string; scheduledEnd?: string; reasonCode?: string; note?: string }) =>
            api.post<{ placement: "scheduled" | "board" }>(`/api/jobs/${job.id}/add-appointment`, body),
        onSuccess: (r) => {
            qc.invalidateQueries({ queryKey: ["job", job.id] });
            qc.invalidateQueries({ queryKey: ["job-appointments", job.id] });
            toast.success(
                r?.placement === "scheduled"
                    ? "Booked — the prior visit is kept in the timeline"
                    : "Back on the schedule board — drag it onto a day",
            );
            resetApptForm();
        },
        onError: (e: unknown) => {
            const msg =
                (e as { body?: { message?: string; error?: string } })?.body?.message ??
                (e as { body?: { error?: string } })?.body?.error ??
                "unknown error";
            toast.error(`Couldn't add appointment: ${msg}`);
        },
    });
    const submitAddAppointment = () => {
        const body: { scheduledStart?: string; scheduledEnd?: string; reasonCode?: string; note?: string } = {};
        if (apptReasonCode) body.reasonCode = apptReasonCode;
        if (apptNote.trim()) body.note = apptNote.trim();
        if (apptPickDate && apptStart) {
            const start = new Date(apptStart);
            let end = apptEnd ? new Date(apptEnd) : null;
            if (start && end && end.getTime() < start.getTime() + 30 * 60_000) {
                end = new Date(start.getTime() + 30 * 60_000);
            }
            body.scheduledStart = start.toISOString();
            if (end) body.scheduledEnd = end.toISOString();
        }
        addAppointment.mutate(body);
    };
    // Offer it on any open job that already has a placement/history to continue
    // from (a never-scheduled job uses "Set schedule" instead).
    const canAddAppointment =
        can(PERMISSIONS.jobs.manage) && !collapsed && (!!job.scheduled_start || appointments.length > 0);

    return (
        <Card ref={cardRef}>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <Calendar className="h-4 w-4" /> Schedule &amp; Crew
                    </CardTitle>
                    {!editing && (
                        <button type="button" className="text-xs text-primary hover:underline" onClick={openEditor}>
                            Edit
                        </button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                {collapsed ? (
                    // Finished job: two-line summary — the page reads like a
                    // report, not a control panel (spec §3.4 / §4). Click
                    // expands to the full booking detail + visit history (#769).
                    <button
                        type="button"
                        data-testid="schedule-collapsed-summary"
                        className="block w-full text-left"
                        title="Show booking details and visit history"
                        onClick={() => setExpandedDone(true)}
                    >
                        <p className={IDENTITY_VALUE_CLS}>
                            {job.scheduled_start ? (
                                <>✓ Visited {formatVisitWindow(job.scheduled_start, job.scheduled_end ?? "")}</>
                            ) : (
                                "No appointment recorded"
                            )}
                        </p>
                        <p className="text-[13px] text-foreground/80">
                            {job.assigned_to_name || "Unassigned"}
                            {crew.length > 0 && ` + ${crewSummaryLabel(crew)}`}
                            {confirmed && " ✓"}
                        </p>
                    </button>
                ) : !editing ? (
                    <div className="space-y-2 text-xs">
                        <div className="flex items-start gap-2">
                            <RowLabel>Visit</RowLabel>
                            {job.scheduled_start ? (
                                <span className={IDENTITY_VALUE_CLS}>
                                    {formatDateTime(job.scheduled_start)}
                                    {job.scheduled_end ? ` – ${formatDateTime(job.scheduled_end)}` : ""}
                                </span>
                            ) : (
                                <span className="pt-0.5 text-foreground/70">Not scheduled</span>
                            )}
                        </div>
                        <div className="flex items-start gap-2">
                            <RowLabel>Tech</RowLabel>
                            {job.assigned_to_name ? (
                                <span className={IDENTITY_VALUE_CLS}>{job.assigned_to_name}</span>
                            ) : (
                                <span className="pt-0.5 text-foreground/70">Unassigned</span>
                            )}
                        </div>
                        {crew.length > 0 && (
                            <div className="flex items-start gap-2">
                                <RowLabel>Crew</RowLabel>
                                <span className="flex flex-wrap items-center gap-1">
                                    {crew.map((c) => (
                                        <span
                                            key={c.user_id}
                                            className="rounded-full border bg-muted px-2 py-0.5 text-[11px]"
                                            title={
                                                c.earning_mode
                                                    ? `${c.role === "tech" ? "Tech" : "Helper"} · ${EARNING_MODE_LABEL[c.earning_mode]}`
                                                    : undefined
                                            }
                                        >
                                            {c.name || "Unknown tech"}
                                            <span className="ml-1 text-muted-foreground capitalize">({c.role})</span>
                                        </span>
                                    ))}
                                </span>
                            </div>
                        )}
                        {/* Issue #1245: adding a second tech used to be an
                            unlabeled dropdown buried in edit mode, so the
                            office reassigned instead and the first tech
                            silently came off the job. Name it, out here. */}
                        {job.assigned_to && !done && (
                            <div className="flex items-start gap-2">
                                <RowLabel>{crew.length > 0 ? "" : "Crew"}</RowLabel>
                                <button
                                    type="button"
                                    onClick={openEditor}
                                    className="inline-flex items-center gap-1 pt-0.5 text-primary hover:underline"
                                >
                                    <Plus className="h-3 w-3" />
                                    {crew.length > 0 ? "Add another" : "Add another tech or helper"}
                                </button>
                            </div>
                        )}
                        <div className="flex items-start gap-2">
                            <RowLabel>Booking</RowLabel>
                            {confirmed ? (
                                <span className="inline-flex items-center gap-1 pt-0.5 text-emerald-700 dark:text-emerald-400">
                                    <Lock className="h-3 w-3" /> Confirmed
                                    {job.confirmed_by_name ? ` · ${job.confirmed_by_name}` : ""}
                                </span>
                            ) : (
                                <span className="pt-0.5 text-foreground/70">Unconfirmed</span>
                            )}
                        </div>
                        <div className="flex items-start gap-2">
                            <RowLabel>Priority</RowLabel>
                            <span className="pt-0.5">
                                {job.priority ?? `Default${job.job_type_priority ? ` — ${job.job_type_priority}` : ""}`}
                            </span>
                        </div>
                    </div>
                ) : (
                    <div className="space-y-3 text-xs">
                        <div>
                            <label className="text-[11px] text-foreground/70">Start</label>
                            <input
                                type="datetime-local"
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                value={schedStart}
                                onChange={(e) => setSchedStart(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-[11px] text-foreground/70">End</label>
                            <input
                                type="datetime-local"
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                value={schedEnd}
                                onChange={(e) => setSchedEnd(e.target.value)}
                            />
                        </div>
                        <div>
                            <label
                                htmlFor="assignee"
                                className="text-[11px] font-medium text-foreground/80 flex items-center gap-1"
                            >
                                <UserCheck className="h-3.5 w-3.5" /> Assigned tech
                            </label>
                            <select
                                id="assignee"
                                className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                disabled={m.updateAssignee.isPending || techsLoading}
                                value={job.assigned_to ?? ""}
                                onChange={(e) => m.updateAssignee.mutate(e.target.value || null)}
                            >
                                <option value="">Unassigned</option>
                                {/* Keep the current assignee selectable even if they're
                                    not in the active technician list (e.g. role change). */}
                                {job.assigned_to && !techs.some((t) => t.id === job.assigned_to) && (
                                    <option value={job.assigned_to}>
                                        {job.assigned_to_name || "Current assignee"}
                                    </option>
                                )}
                                {techs.map((t) => (
                                    <option key={t.id} value={t.id}>
                                        {techLabel(t)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <span className="text-[11px] text-foreground/70 flex items-center gap-1">
                                <Users className="h-3 w-3" /> Crew
                            </span>
                            {/* Lead row (read-only here — changed via Assigned tech above). */}
                            {job.assigned_to && (
                                <p className="mt-1 flex items-center gap-1.5 text-xs">
                                    <span className="truncate">{job.assigned_to_name || "Assigned tech"}</span>
                                    <span className="rounded-full border border-primary/50 bg-primary/10 px-1.5 py-0 text-[10px] font-medium text-primary">
                                        Lead
                                    </span>
                                </p>
                            )}
                            {crew.length > 0 && (
                                <ul className="mt-1 space-y-1">
                                    {crew.map((c) => (
                                        <li key={c.user_id} className="flex items-center gap-1.5 text-xs">
                                            <span className="min-w-0 flex-1 truncate">{c.name || "Unknown tech"}</span>
                                            <span className="rounded-full border px-1.5 py-0 text-[10px] capitalize text-foreground/70">
                                                {c.role === "tech" ? "Tech" : "Helper"}
                                            </span>
                                            {c.earning_mode && (
                                                <span
                                                    className="rounded-full border border-sky-500/40 bg-sky-500/10 px-1.5 py-0 text-[10px] text-sky-700 dark:text-sky-300"
                                                    title="Points earning mode (chosen when added)"
                                                >
                                                    {EARNING_MODE_LABEL[c.earning_mode]}
                                                </span>
                                            )}
                                            <button
                                                type="button"
                                                className="text-destructive hover:opacity-80 disabled:opacity-50"
                                                disabled={m.removeHelper.isPending}
                                                onClick={() => m.removeHelper.mutate(c.user_id)}
                                                aria-label="Remove crew member"
                                            >
                                                <X className="h-3.5 w-3.5" />
                                            </button>
                                        </li>
                                    ))}
                                </ul>
                            )}
                            <div className="mt-1 flex gap-1">
                                <select
                                    className="flex-1 rounded-md border bg-background px-2 py-1 text-xs"
                                    value={helperInput}
                                    onChange={(e) => {
                                        setHelperInput(e.target.value);
                                        setCrewFullPrompt(null);
                                    }}
                                >
                                    <option value="">Add tech or helper…</option>
                                    {techs
                                        .filter(
                                            (t) => t.id !== job.assigned_to && !crew.some((c) => c.user_id === t.id),
                                        )
                                        .map((t) => (
                                            <option key={t.id} value={t.id}>
                                                {techLabel(t)}
                                            </option>
                                        ))}
                                </select>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={!helperInput || m.addHelper.isPending}
                                    onClick={() => {
                                        if (!helperInput) return;
                                        const payload: AddCrewPayload = {
                                            userId: helperInput,
                                            role: addRole,
                                            earningMode: addMode,
                                        };
                                        m.addHelper.mutate(payload, {
                                            onSuccess: () => {
                                                setHelperInput("");
                                                setCrewFullPrompt(null);
                                                setModeTouched(false);
                                            },
                                            onError: (e) => {
                                                const body = (e as { body?: Record<string, unknown> } | null)?.body;
                                                if (body?.error === "CREW_FULL") {
                                                    const crewSize = Number(body.crewSize ?? 1);
                                                    const override = body.override as
                                                        { raiseCrewSizeTo?: number } | undefined;
                                                    setCrewFullPrompt({
                                                        payload,
                                                        crewSize,
                                                        raiseTo: override?.raiseCrewSizeTo ?? crewSize + 1,
                                                    });
                                                }
                                            },
                                        });
                                    }}
                                >
                                    <Plus className="h-3.5 w-3.5" />
                                </Button>
                            </div>
                            {helperInput && (
                                <div className="mt-1.5 space-y-1.5">
                                    <div className="flex items-center gap-1">
                                        {(["tech", "helper"] as const).map((r) => (
                                            <button
                                                key={r}
                                                type="button"
                                                onClick={() => {
                                                    setAddRole(r);
                                                    // Mode default follows the role until the
                                                    // office explicitly picks a chip.
                                                    if (!modeTouched) setAddMode(r === "tech" ? "full" : "mirror");
                                                }}
                                                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium capitalize transition-colors ${
                                                    addRole === r
                                                        ? "border-primary bg-primary text-primary-foreground"
                                                        : "text-foreground/70 hover:border-primary/50"
                                                }`}
                                            >
                                                {r}
                                            </button>
                                        ))}
                                        <span className="text-[10px] text-muted-foreground">·</span>
                                        {(["full", "split_even", "mirror"] as const).map((mode) => (
                                            <button
                                                key={mode}
                                                type="button"
                                                onClick={() => {
                                                    setAddMode(mode);
                                                    setModeTouched(true);
                                                }}
                                                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                                    addMode === mode
                                                        ? "border-sky-500 bg-sky-500/15 text-sky-700 dark:text-sky-300"
                                                        : "text-foreground/70 hover:border-sky-500/50"
                                                }`}
                                                title="How this person earns points on this job"
                                            >
                                                {EARNING_MODE_LABEL[mode]}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                            {/* CREW_FULL → one-tap inline override, never a dead end. */}
                            {crewFullPrompt && (
                                <div className="mt-1.5 space-y-1.5 rounded-md border border-amber-400/60 bg-amber-500/10 p-2">
                                    <p className="text-[11px] font-medium text-amber-800 dark:text-amber-200">
                                        Crew size is {crewFullPrompt.crewSize} — add anyway?
                                    </p>
                                    <input
                                        className="w-full rounded-md border bg-background px-2 py-1 text-[11px]"
                                        placeholder="Reason (optional)"
                                        value={overrideReason}
                                        onChange={(e) => setOverrideReason(e.target.value)}
                                    />
                                    <div className="flex items-center gap-1.5">
                                        <Button
                                            size="sm"
                                            className="h-6 px-2 text-[11px]"
                                            disabled={m.addHelper.isPending}
                                            onClick={() =>
                                                m.addHelper.mutate(
                                                    {
                                                        ...crewFullPrompt.payload,
                                                        force: true,
                                                        raiseCrewSizeTo: crewFullPrompt.raiseTo,
                                                        reason: overrideReason.trim() || undefined,
                                                    },
                                                    {
                                                        onSuccess: () => {
                                                            setHelperInput("");
                                                            setCrewFullPrompt(null);
                                                            setOverrideReason("");
                                                            setModeTouched(false);
                                                        },
                                                    },
                                                )
                                            }
                                        >
                                            Add + raise to {crewFullPrompt.raiseTo}
                                        </Button>
                                        <Button
                                            size="sm"
                                            variant="ghost"
                                            className="h-6 px-2 text-[11px]"
                                            onClick={() => setCrewFullPrompt(null)}
                                        >
                                            Cancel
                                        </Button>
                                    </div>
                                </div>
                            )}
                        </div>
                        <div>
                            <label htmlFor="job-priority" className="text-[11px] text-foreground/70">
                                Priority
                            </label>
                            <select
                                id="job-priority"
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                disabled={m.updateJobFields.isPending}
                                value={job.priority ?? "__default__"}
                                onChange={(e) => {
                                    const v = e.target.value;
                                    m.updateJobFields.mutate({ priority: v === "__default__" ? null : v });
                                }}
                            >
                                <option value="__default__">
                                    Default{job.job_type_priority ? ` — ${job.job_type_priority}` : ""}
                                </option>
                                <option value="low">Low</option>
                                <option value="normal">Normal</option>
                                <option value="high">High</option>
                            </select>
                        </div>
                        <div className="flex items-center justify-between gap-2">
                            <span className="text-[11px] font-medium text-foreground/80 flex items-center gap-1">
                                {confirmed ? (
                                    <Lock className="h-3.5 w-3.5 text-emerald-600" />
                                ) : (
                                    <CheckCircle2 className="h-3.5 w-3.5" />
                                )}
                                Booking
                            </span>
                            <Button
                                size="sm"
                                variant={confirmed ? "outline" : "default"}
                                disabled={m.toggleConfirm.isPending}
                                onClick={() => m.toggleConfirm.mutate(!confirmed)}
                            >
                                {m.toggleConfirm.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                                {confirmed ? "Unconfirm" : "Confirm"}
                            </Button>
                        </div>
                        <p className="text-[11px] text-foreground/70">
                            {confirmed
                                ? `Locked from the auto-scheduler${job.confirmed_by_name ? ` · ${job.confirmed_by_name}` : ""}${job.confirmed_at ? ` · ${formatDate(job.confirmed_at)}` : ""}`
                                : "Confirm to lock this slot so the auto-scheduler won't move it."}
                        </p>
                        <div className="flex gap-2 pt-1">
                            <Button
                                size="sm"
                                disabled={m.updateSchedule.isPending}
                                onClick={() => {
                                    const start = schedStart ? new Date(schedStart) : null;
                                    let end = schedEnd ? new Date(schedEnd) : null;
                                    // 30 minutes is the smallest window we schedule
                                    // (G3 amendment) — shorter entries get extended.
                                    if (start && end && end.getTime() < start.getTime() + 30 * 60_000) {
                                        end = new Date(start.getTime() + 30 * 60_000);
                                        toast.info("Appointments have a 30-minute minimum — end time adjusted.");
                                    }
                                    m.updateSchedule.mutate(
                                        {
                                            scheduledStart: start ? start.toISOString() : null,
                                            scheduledEnd: end ? end.toISOString() : null,
                                        },
                                        { onSuccess: () => setEditing(false) },
                                    );
                                }}
                            >
                                {m.updateSchedule.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                                Save schedule
                            </Button>
                            <Button size="sm" variant="outline" onClick={() => setEditing(false)}>
                                Done
                            </Button>
                        </div>
                    </div>
                )}
                {!editing && !postVisit && !job.scheduled_start && !canAddAppointment && (
                    <EmptyRow label="Not scheduled yet" actionLabel="Set schedule" onAction={openEditor} />
                )}
                {/* Multi-visit toggle (#293 → relocated here by #769): several
                    trips, crew submits "today's work" per visit, the rest
                    carries forward. Pre-visit / in-progress only — once the
                    visit happened this is history, not a decision. */}
                {!editing && !collapsed && !done && !postVisit && (
                    <label
                        data-testid="multi-visit-toggle"
                        className="mt-3 flex cursor-pointer items-start gap-2 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs"
                    >
                        <input
                            type="checkbox"
                            className="mt-0.5 accent-primary"
                            checked={!!job.is_multi_visit}
                            disabled={m.updateJobFields.isPending}
                            onChange={(e) => m.updateJobFields.mutate({ isMultiVisit: e.target.checked })}
                        />
                        <span>
                            <span className="font-semibold text-foreground">Multi-visit job</span>{" "}
                            <span className="text-muted-foreground">
                                — several trips. The crew submits the devices they finish each visit; the rest carry
                                forward until every device is done.
                            </span>
                        </span>
                    </label>
                )}
                {/* Add another appointment (office-only). The current visit didn't
                    finish → end it honestly and set up the next. Default drops the
                    job back on the schedule board to be dragged onto a day; a date
                    is the optional exception. */}
                {canAddAppointment && !editing && (
                    <div className="mt-3">
                        {!addingAppt ? (
                            <button
                                type="button"
                                onClick={() => setAddingAppt(true)}
                                className="flex w-full items-center justify-center gap-1.5 rounded-lg border border-dashed border-primary/60 bg-primary/5 px-3 py-2 text-xs font-semibold text-primary hover:bg-primary/10"
                            >
                                <CalendarPlus className="h-3.5 w-3.5" /> Add another appointment
                            </button>
                        ) : (
                            <div className="space-y-3 rounded-lg border bg-muted/40 p-3">
                                <div>
                                    <p className="text-xs font-semibold">Add another appointment</p>
                                    <p className="mt-0.5 text-[11px] leading-relaxed text-foreground/60">
                                        Keeps this job open and logs the current visit as{" "}
                                        <span className="font-medium text-amber-700 dark:text-amber-400">
                                            didn't complete
                                        </span>{" "}
                                        in the timeline. By default it goes back on the schedule board to drag onto a
                                        day.
                                    </p>
                                </div>
                                <div>
                                    <label className="text-[11px] text-foreground/70">
                                        Why didn't this visit finish? (optional)
                                    </label>
                                    <div className="mt-1 flex flex-wrap gap-1">
                                        {ADD_APPOINTMENT_REASONS.map((r) => (
                                            <button
                                                key={r.code}
                                                type="button"
                                                onClick={() =>
                                                    setApptReasonCode(apptReasonCode === r.code ? null : r.code)
                                                }
                                                className={`rounded-full border px-2 py-0.5 text-[10px] font-medium transition-colors ${
                                                    apptReasonCode === r.code
                                                        ? "border-amber-500 bg-amber-500/15 text-amber-700 dark:text-amber-300"
                                                        : "text-foreground/70 hover:border-amber-500/50"
                                                }`}
                                            >
                                                {r.label}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                                <div>
                                    <label className="text-[11px] text-foreground/70">Note (optional)</label>
                                    <Textarea
                                        rows={2}
                                        value={apptNote}
                                        onChange={(e) => setApptNote(e.target.value)}
                                        className="mt-0.5 text-xs"
                                        placeholder="Anything the office should know…"
                                    />
                                </div>
                                <label className="flex items-center gap-1.5 text-[11px] text-foreground/70">
                                    <input
                                        type="checkbox"
                                        checked={apptPickDate}
                                        onChange={(e) => setApptPickDate(e.target.checked)}
                                    />
                                    Pick a date now (otherwise it goes to the board)
                                </label>
                                {apptPickDate && (
                                    <div className="space-y-2">
                                        <div>
                                            <label className="text-[11px] text-foreground/70">Start</label>
                                            <input
                                                type="datetime-local"
                                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                                value={apptStart}
                                                onChange={(e) => setApptStart(e.target.value)}
                                            />
                                        </div>
                                        <div>
                                            <label className="text-[11px] text-foreground/70">End</label>
                                            <input
                                                type="datetime-local"
                                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                                value={apptEnd}
                                                onChange={(e) => setApptEnd(e.target.value)}
                                            />
                                        </div>
                                    </div>
                                )}
                                <div className="flex items-center gap-2">
                                    <Button
                                        size="sm"
                                        disabled={addAppointment.isPending || (apptPickDate && !apptStart)}
                                        onClick={submitAddAppointment}
                                    >
                                        {addAppointment.isPending && (
                                            <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />
                                        )}
                                        {apptPickDate ? "Book appointment" : "Add to schedule board"}
                                    </Button>
                                    <Button
                                        size="sm"
                                        variant="ghost"
                                        onClick={resetApptForm}
                                        disabled={addAppointment.isPending}
                                    >
                                        Cancel
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}
                {/* Issue #442/#454: the visit timeline. Every appointment this job has
                    had — who was dispatched, when, and how it ended — newest first.
                    Shows as soon as any visit has ended, so the "didn't complete →
                    current" story is always visible, never buried. On a FINISHED
                    job it lives inside the done-state collapse (#769) — expand
                    the 2-line summary to see it. */}
                {!collapsed && appointments.some((a) => a.endedAt || a.supersededAt) && (
                    <div className="mt-3 border-t pt-3">
                        <p className="mb-2 flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-foreground/60">
                            <Calendar className="h-3 w-3" /> Visit history
                        </p>
                        <ul className="pl-1">
                            {appointments.map((a) => {
                                // Live/current = not superseded and not ended (Issue #454).
                                const isCurrent = !a.supersededAt && !a.endedAt;
                                const outcome = a.outcome ?? "";
                                const outcomeLabel = APPOINTMENT_OUTCOME_LABEL[outcome] ?? "Ended";
                                const tone = APPOINTMENT_OUTCOME_TONE[outcome] ?? "border-border text-muted-foreground";
                                const dot = isCurrent
                                    ? "bg-emerald-600"
                                    : outcome === "returned"
                                      ? "bg-amber-500"
                                      : outcome === "cannot_complete"
                                        ? "bg-red-500"
                                        : "bg-foreground/30";
                                const names = [
                                    a.assignedTechName,
                                    ...a.crew.filter((c) => !c.isPrimary).map((c) => c.name),
                                ].filter((n): n is string => !!n);
                                return (
                                    <li
                                        key={a.id}
                                        className="relative border-l-2 border-border pb-3 pl-4 last:border-transparent last:pb-0"
                                    >
                                        <span
                                            className={`absolute -left-[5px] top-1 h-2.5 w-2.5 rounded-full border-2 border-card ${dot}`}
                                        />
                                        <div className="flex items-center justify-between gap-2">
                                            <span
                                                className={
                                                    isCurrent ? "text-xs font-semibold" : "text-xs text-foreground/60"
                                                }
                                            >
                                                {formatVisitWindow(a.startAt, a.endAt)}
                                            </span>
                                            <span
                                                className={`shrink-0 rounded-full border px-1.5 py-0 text-[10px] ${
                                                    isCurrent
                                                        ? "border-emerald-500/40 bg-emerald-500/10 text-emerald-700 dark:text-emerald-400"
                                                        : tone
                                                }`}
                                            >
                                                {isCurrent ? "Current" : outcomeLabel}
                                            </span>
                                        </div>
                                        <p className="text-[11px] text-foreground/60">
                                            {names.length > 0 ? names.join(", ") : "Unassigned"}
                                        </p>
                                        {!isCurrent && a.endedReason && (
                                            <p className="mt-0.5 text-[11px] italic text-amber-700 dark:text-amber-400">
                                                &ldquo;{a.endedReason}&rdquo;
                                            </p>
                                        )}
                                    </li>
                                );
                            })}
                        </ul>
                    </div>
                )}
                {/* Issue #454: office-only terminal decision. The tech never marks
                    the whole job cannot-complete — they return the trip and the
                    office decides here (or reschedules = adds another appointment). */}
                {canOfficeCannotComplete &&
                    (showCannotComplete ? (
                        <div className="mt-3 space-y-2 border-t pt-3">
                            <label className="block text-[11px] font-medium text-foreground/80">
                                Why can't this job be completed?
                            </label>
                            <textarea
                                rows={2}
                                className="w-full rounded-md border bg-background px-2 py-1 text-xs"
                                value={cannotCompleteReason}
                                onChange={(e) => setCannotCompleteReason(e.target.value)}
                                placeholder="Customer cancelled, site demolished, out of scope…"
                                autoFocus
                            />
                            <div className="flex items-center justify-end gap-2">
                                <Button
                                    size="sm"
                                    variant="ghost"
                                    onClick={() => setShowCannotComplete(false)}
                                    disabled={cannotComplete.isPending}
                                >
                                    Cancel
                                </Button>
                                <Button
                                    size="sm"
                                    variant="destructive"
                                    onClick={() => cannotComplete.mutate(cannotCompleteReason.trim())}
                                    disabled={cannotComplete.isPending || cannotCompleteReason.trim().length === 0}
                                >
                                    {cannotComplete.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                                    Mark cannot-complete
                                </Button>
                            </div>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="mt-3 w-full border-t pt-3 text-left text-[11px] text-destructive/80 hover:text-destructive"
                            onClick={() => setShowCannotComplete(true)}
                        >
                            Job can't be completed at all? Mark cannot-complete →
                        </button>
                    ))}
                {/* #769: way back down from the expanded done state. */}
                {done && expandedDone && !editing && (
                    <button
                        type="button"
                        className="mt-3 w-full border-t pt-3 text-left text-[11px] text-foreground/60 hover:text-foreground"
                        onClick={() => setExpandedDone(false)}
                    >
                        Show less
                    </button>
                )}
            </CardContent>
        </Card>
    );
}

// ── Details ─────────────────────────────────────────────────────────────────

export function DetailsCard({
    job,
    jobTypes,
    jobTypesLoading,
    onWantJobTypes,
    m,
}: {
    job: JobDetail;
    jobTypes: Array<{ id: string; name: string; trade_name?: string | null }>;
    jobTypesLoading: boolean;
    onWantJobTypes: () => void;
    m: RailMutations;
}) {
    const [editing, setEditing] = useState(false);
    const [jtInput, setJtInput] = useState("");
    const [flexInput, setFlexInput] = useState("");
    const [windowInput, setWindowInput] = useState("");
    const [dueByInput, setDueByInput] = useState("");
    const [poInput, setPoInput] = useState("");
    const [woInput, setWoInput] = useState("");
    const [descInput, setDescInput] = useState("");
    const [tagInput, setTagInput] = useState("");

    return (
        <Card>
            <CardHeader className="pb-3">
                <div className="flex items-center justify-between gap-2">
                    <CardTitle className="text-base flex items-center gap-2">
                        <FileText className="h-4 w-4" /> Details
                    </CardTitle>
                    {!editing && (
                        <button
                            type="button"
                            className="text-xs text-primary hover:underline"
                            onClick={() => {
                                setJtInput(job.job_type_id ?? "");
                                setFlexInput(job.scheduling_flexibility ?? "");
                                setWindowInput(job.preferred_window ?? "");
                                setDueByInput(job.due_by_date ?? "");
                                setPoInput(job.customer_po ?? "");
                                setWoInput(job.external_ref ?? "");
                                setDescInput(job.customer_facing_description ?? "");
                                onWantJobTypes();
                                setEditing(true);
                            }}
                        >
                            Edit
                        </button>
                    )}
                </div>
            </CardHeader>
            <CardContent className="space-y-1 text-xs">
                {!editing ? (
                    <div className="space-y-0.5">
                        {/* Job type is identity ("what kind of work is this") —
                            it gets the loud treatment like the rail's other
                            who/what/where values (#769). */}
                        <div className="flex items-start gap-2 pb-1">
                            <RowLabel>Type</RowLabel>
                            <span className={IDENTITY_VALUE_CLS}>{job.job_type_name ?? "—"}</span>
                        </div>
                        <p>
                            <span className="text-foreground/70">Flexibility:</span>{" "}
                            {SCHEDULING_FLEXIBILITY_LABEL[job.scheduling_flexibility ?? ""] ??
                                job.scheduling_flexibility ??
                                "—"}
                        </p>
                        <p>
                            <span className="text-foreground/70">Window:</span> {job.preferred_window ?? "Anytime"}
                        </p>
                        <p>
                            <span className="text-foreground/70">Due by:</span>{" "}
                            {job.due_by_date ? formatDateOnly(job.due_by_date) : "—"}
                        </p>
                        {/* Prompt 23 item 3: a WO complete-by that's already
                            past is informational (not an alarm) when the
                            office knowingly scheduled the visit after it. */}
                        {job.due_by_date &&
                            classifyDueState(job.due_by_date, job.scheduled_start) === "scheduled_past_due" && (
                                <p
                                    className="inline-flex rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-medium text-slate-700 dark:bg-slate-700/50 dark:text-slate-300"
                                    data-testid="wo-scheduled-past-due-chip"
                                >
                                    {scheduledPastDueTitle(job.due_by_date)}
                                </p>
                            )}
                        {job.customer_po && (
                            <p className="tabular-nums">
                                <span className="text-foreground/70">Customer PO:</span> {job.customer_po}
                            </p>
                        )}
                        {job.external_ref && (
                            <p className="tabular-nums">
                                <span className="text-foreground/70">WO #:</span> {job.external_ref}
                            </p>
                        )}
                        {/* #887 — this account pays against their WO number; the
                            job can't be completed until one is on the card. */}
                        {job.customer_requires_wo && !job.external_ref && !job.customer_po && (
                            <p
                                className="inline-flex rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-medium text-amber-800 dark:bg-amber-900/40 dark:text-amber-300"
                                data-testid="wo-required-chip"
                            >
                                WO # needed before close-out
                            </p>
                        )}
                        {job.customer_facing_description && (
                            <p>
                                <span className="text-foreground/70">Customer description:</span>{" "}
                                {job.customer_facing_description}
                            </p>
                        )}
                        <p>
                            <span className="text-foreground/70">Created:</span> {formatDateTime(job.created_at)}
                        </p>
                        <p>
                            <span className="text-foreground/70">Updated:</span> {formatDate(job.updated_at)}
                        </p>
                    </div>
                ) : (
                    <div className="space-y-2">
                        <div>
                            <label className="text-[11px] text-foreground/70">Job type</label>
                            <select
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                value={jtInput}
                                disabled={jobTypesLoading}
                                onChange={(e) => setJtInput(e.target.value)}
                            >
                                <option value="">— Select —</option>
                                {jobTypes.map((jt) => (
                                    <option key={jt.id} value={jt.id}>
                                        {jt.trade_name ? `${jt.trade_name} · ${jt.name}` : jt.name}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div>
                            <label className="text-[11px] text-foreground/70">Flexibility</label>
                            <select
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                value={flexInput}
                                onChange={(e) => setFlexInput(e.target.value)}
                            >
                                <option value="">— Unset —</option>
                                <option value="flexible">Flexible</option>
                                <option value="specific_day">Specific day</option>
                                <option value="specific_window">Specific window</option>
                                <option value="hard_appointment">Hard appointment</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[11px] text-foreground/70">Preferred window</label>
                            <select
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                value={windowInput}
                                onChange={(e) => setWindowInput(e.target.value)}
                            >
                                <option value="">— Unset —</option>
                                <option value="ANYTIME">Anytime</option>
                                <option value="AM">AM</option>
                                <option value="PM">PM</option>
                                <option value="EVENING">Evening</option>
                            </select>
                        </div>
                        <div>
                            <label className="text-[11px] text-foreground/70">Due by</label>
                            <input
                                type="date"
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                value={dueByInput}
                                onChange={(e) => setDueByInput(e.target.value)}
                            />
                        </div>
                        {/* Optional PO/WO reference — prints at the top of this
                            job's invoices/estimates when set (Issue #383). */}
                        <div>
                            <label className="text-[11px] text-foreground/70">Customer PO #</label>
                            <input
                                type="text"
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                placeholder="Customer's purchase-order number (optional)"
                                value={poInput}
                                onChange={(e) => setPoInput(e.target.value)}
                            />
                        </div>
                        <div>
                            <label className="text-[11px] text-foreground/70">Work order #</label>
                            <input
                                type="text"
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                placeholder="Vendor work-order / service-order number (optional)"
                                value={woInput}
                                onChange={(e) => setWoInput(e.target.value)}
                            />
                        </div>
                        {/* #887 — the one line a statement/invoice may print.
                            Door codes and urgency stay in the tech title. */}
                        <div>
                            <label className="text-[11px] text-foreground/70">Customer description</label>
                            <input
                                type="text"
                                maxLength={200}
                                className="mt-0.5 w-full rounded-md border bg-background px-2 py-1 text-xs"
                                placeholder={
                                    job.job_type_name && job.site_name
                                        ? `e.g. ${job.job_type_name} — ${job.site_name}`
                                        : "Short line for statements (optional)"
                                }
                                value={descInput}
                                onChange={(e) => setDescInput(e.target.value)}
                            />
                        </div>
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                disabled={m.updateJobFields.isPending}
                                onClick={() => {
                                    const vars: Record<string, unknown> = {};
                                    if ((job.job_type_id ?? "") !== jtInput && jtInput) vars.jobTypeId = jtInput;
                                    if ((job.scheduling_flexibility ?? "") !== flexInput && flexInput)
                                        vars.schedulingFlexibility = flexInput;
                                    if ((job.preferred_window ?? "") !== windowInput)
                                        vars.preferredWindow = windowInput || null;
                                    if ((job.due_by_date ?? "") !== dueByInput) vars.dueBy = dueByInput || null;
                                    if ((job.customer_po ?? "") !== poInput.trim())
                                        vars.customerPo = poInput.trim() || null;
                                    if ((job.external_ref ?? "") !== woInput.trim())
                                        vars.externalRef = woInput.trim() || null;
                                    if ((job.customer_facing_description ?? "") !== descInput.trim())
                                        vars.customerFacingDescription = descInput.trim() || null;
                                    if (Object.keys(vars).length === 0) {
                                        setEditing(false);
                                        return;
                                    }
                                    m.updateJobFields.mutate(vars, { onSuccess: () => setEditing(false) });
                                }}
                            >
                                {m.updateJobFields.isPending && <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" />}
                                Save
                            </Button>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={m.updateJobFields.isPending}
                                onClick={() => setEditing(false)}
                            >
                                Cancel
                            </Button>
                        </div>
                    </div>
                )}
                <div className="pt-2 mt-2 border-t">
                    <span className="text-[11px] text-foreground/70 flex items-center gap-1">
                        <Tag className="h-3 w-3" /> Tags
                    </span>
                    <div className="mt-1 flex flex-wrap gap-1">
                        {(job.tags ?? []).map((tg) => (
                            <span
                                key={tg}
                                className="inline-flex items-center gap-1 rounded-full border bg-muted px-2 py-0.5 text-[11px]"
                            >
                                {tg}
                                <button
                                    type="button"
                                    className="text-muted-foreground hover:text-destructive disabled:opacity-50"
                                    disabled={m.updateJobFields.isPending}
                                    onClick={() =>
                                        m.updateJobFields.mutate({
                                            tags: (job.tags ?? []).filter((x) => x !== tg),
                                        })
                                    }
                                    aria-label={`Remove tag ${tg}`}
                                >
                                    <X className="h-3 w-3" />
                                </button>
                            </span>
                        ))}
                    </div>
                    <div className="mt-1">
                        <Input
                            value={tagInput}
                            onChange={(e) => setTagInput(e.target.value)}
                            onKeyDown={(e) => {
                                if (e.key === "Enter") {
                                    e.preventDefault();
                                    const t = tagInput.trim();
                                    if (!t) return;
                                    if ((job.tags ?? []).some((x) => x.toLowerCase() === t.toLowerCase())) {
                                        setTagInput("");
                                        return;
                                    }
                                    m.updateJobFields.mutate({ tags: [...(job.tags ?? []), t] });
                                    setTagInput("");
                                }
                            }}
                            placeholder="Add tag + Enter"
                            className="h-7 text-xs"
                        />
                    </div>
                </div>
                <div className="pt-2 mt-2 border-t">
                    <div className="flex items-center justify-between gap-2">
                        <label
                            htmlFor="helper-value"
                            className="text-[11px] font-medium text-foreground/80"
                            title="Tells the auto-scheduler whether this job should land on a paired-lead (helper) day. Auto resolves from the job type, estimate $, and pricebook tags."
                        >
                            Helper value
                        </label>
                        {job.helper_value && (
                            <span
                                className={`inline-block rounded-full border px-1.5 py-0 text-[10px] font-semibold ${HELPER_VALUE_BADGE_CLS[job.helper_value]}`}
                                title={
                                    job.helper_value_override
                                        ? "Manual override is active for this job."
                                        : `Resolved automatically (job-type baseline: ${HELPER_VALUE_LABEL[job.helper_value_default ?? "medium"]}).`
                                }
                            >
                                {HELPER_VALUE_LABEL[job.helper_value]}
                            </span>
                        )}
                    </div>
                    <select
                        id="helper-value"
                        className="mt-1 w-full rounded-md border bg-background px-2 py-1 text-xs"
                        disabled={m.updateHelperValue.isPending}
                        value={job.helper_value_override ?? "__auto__"}
                        onChange={(e) => {
                            const v = e.target.value;
                            m.updateHelperValue.mutate(v === "__auto__" ? null : (v as HelperValueTier));
                        }}
                    >
                        <option value="__auto__">
                            Auto{job.helper_value ? ` — ${HELPER_VALUE_LABEL[job.helper_value]}` : ""}
                        </option>
                        <option value="low">Low — routine testing</option>
                        <option value="medium">Medium — service / small repair</option>
                        <option value="high">High — repair / replace / install</option>
                    </select>
                </div>
                <AiButtons jobId={job.id} />
            </CardContent>
        </Card>
    );
}

// ── AI: two small buttons, output in a drawer (spec §3.4) ───────────────────

interface EstimateSuggestion {
    description: string;
    reasoning?: string;
    unitPrice: number | string;
    quantity: number | string;
}

function AiButtons({ jobId }: { jobId: string }) {
    const [open, setOpen] = useState(false);
    const [summary, setSummary] = useState<string | null>(null);
    const [suggestions, setSuggestions] = useState<EstimateSuggestion[] | null>(null);

    const summaryMutation = useMutation({
        mutationFn: () => api.get<{ summary: string }>(`/api/ai/jobs/${jobId}/summary`),
        onSuccess: (data) => {
            setSummary(data.summary);
            setSuggestions(null);
            setOpen(true);
        },
    });

    const estimateMutation = useMutation({
        mutationFn: () => api.get<{ suggestions: EstimateSuggestion[] }>(`/api/ai/jobs/${jobId}/estimate-suggestions`),
        onSuccess: (data) => {
            setSuggestions(data.suggestions);
            setSummary(null);
            setOpen(true);
        },
    });

    return (
        <div className="pt-2 mt-2 border-t">
            <div className="flex items-center gap-1.5">
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => summaryMutation.mutate()}
                    disabled={summaryMutation.isPending}
                >
                    {summaryMutation.isPending ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                        <Sparkles className="h-3 w-3 mr-1 text-amber-500" />
                    )}
                    AI summary
                </Button>
                <Button
                    variant="outline"
                    size="sm"
                    className="h-7 text-xs"
                    onClick={() => estimateMutation.mutate()}
                    disabled={estimateMutation.isPending}
                >
                    {estimateMutation.isPending ? (
                        <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                    ) : (
                        <FileEdit className="h-3 w-3 mr-1" />
                    )}
                    Estimate lines
                </Button>
            </div>
            <Sheet open={open} onOpenChange={setOpen}>
                <SheetContent className="w-[min(96vw,560px)] sm:max-w-none overflow-y-auto">
                    <SheetHeader>
                        <SheetTitle className="text-sm flex items-center gap-2">
                            <Sparkles className="h-4 w-4 text-amber-500" />
                            {summary ? "AI Job Summary" : "Suggested Estimate Lines"}
                        </SheetTitle>
                    </SheetHeader>
                    <div className="mt-4 space-y-3">
                        {summary && <p className="text-sm whitespace-pre-wrap">{summary}</p>}
                        {suggestions &&
                            (suggestions.length === 0 ? (
                                <p className="text-sm text-foreground/70">No suggestions for this job.</p>
                            ) : (
                                suggestions.map((s, i) => (
                                    <div
                                        key={i}
                                        className="flex items-start justify-between rounded-md border p-3 text-sm"
                                    >
                                        <div>
                                            <p className="font-medium">{s.description}</p>
                                            {s.reasoning && <p className="text-xs text-foreground/80">{s.reasoning}</p>}
                                        </div>
                                        <div className="text-right shrink-0 ml-4">
                                            <p className="font-mono">${Number(s.unitPrice).toFixed(2)}</p>
                                            <p className="text-xs text-foreground/80">x{s.quantity}</p>
                                        </div>
                                    </div>
                                ))
                            ))}
                    </div>
                </SheetContent>
            </Sheet>
        </div>
    );
}

// ── Rail ────────────────────────────────────────────────────────────────────

/**
 * Right rail (spec §3.4 + §6d.1): compact, read-mostly reference in a FIXED
 * order — (1) Customer & Site, (2) Schedule & Crew, (3) Water & Reporting,
 * then everything else. Who and where is always the first thing the eye
 * finds. The Work Order PDF card always lives here (see WorkOrderMappingCard)
 * — loud while its mapping is unverified, quiet once confirmed — so its
 * "Review / fix PDF mapping" entry point never moves.
 */
export function ReferenceRail({
    job,
    postVisit,
    techs,
    techsLoading,
    jobTypes,
    jobTypesLoading,
    onWantJobTypes,
    mutations,
    workOrder,
    scheduleEditNonce,
}: {
    job: JobDetail;
    postVisit: boolean;
    techs: TechOption[];
    techsLoading: boolean;
    jobTypes: Array<{ id: string; name: string; trade_name?: string | null }>;
    jobTypesLoading: boolean;
    onWantJobTypes: () => void;
    mutations: RailMutations;
    workOrder?: WorkOrderMappingInfo | null;
    /** Increment to pop the Schedule & Crew card into edit mode (header
     *  "Schedule" on an unscheduled job routes here — Prompt 21 3c). */
    scheduleEditNonce?: number;
}) {
    const schedule = (
        <ScheduleCrewCard
            key="schedule"
            job={job}
            postVisit={postVisit}
            techs={techs}
            techsLoading={techsLoading}
            m={mutations}
            editNonce={scheduleEditNonce}
        />
    );
    const customer = <CustomerSiteCard key="customer" job={job} />;
    return (
        <div className="space-y-4">
            {customer}
            {schedule}
            <WaterReportingCard job={job} />
            {workOrder && <WorkOrderMappingCard wo={workOrder} />}
            <DetailsCard
                job={job}
                jobTypes={jobTypes}
                jobTypesLoading={jobTypesLoading}
                onWantJobTypes={onWantJobTypes}
                m={mutations}
            />
        </div>
    );
}

// ── Work Order mapping ──────────────────────────────────────────────────────
// Mapping health of the vendor's return-PDF template + one-click entry into
// the WO review page's Fix-template-mapping mode. Auto-created jobs skip the
// review screen entirely, so this card is the office's first (and only)
// pre-visit checkpoint for where values will stamp on the return PDF.

/** Mapping counts as verified only when requirements are confirmed AND at
 *  least one field is actually mapped on the return PDF (spec §6d.2). */
export function woMappingVerified(wo: WorkOrderMappingInfo): boolean {
    return wo.requirements_confirmed === true && (wo.mapped_field_count ?? 0) > 0;
}

/**
 * The work order's return-PDF mapping card — ONE stable home in the rail,
 * always rendered for a WO job. Loud (amber border + primary action + the
 * pre-dispatch warning) while the mapping is unverified, quiet once confirmed.
 *
 * This used to be split across two places: a loud amber gate at the top of the
 * MAIN column while unverified, and this quiet rail card once verified — so the
 * "Review / fix PDF mapping" entry point jumped between the top-left and the
 * rail as a job's state changed, and the office had to hunt for it. Unifying to
 * a single rail card keeps the same prominence when action is needed without
 * ever moving where the entry point lives.
 */
export function WorkOrderMappingCard({ wo }: { wo: WorkOrderMappingInfo }) {
    const fieldCount = wo.mapped_field_count ?? 0;
    const confirmed = wo.requirements_confirmed === true && fieldCount > 0;
    const hasTemplate = !!wo.template_id;
    const needsReview = !confirmed;

    return (
        <Card
            data-testid="wo-mapping-rail-card"
            className={needsReview ? "border-amber-400 dark:border-amber-500/70" : undefined}
        >
            <CardHeader className="pb-2">
                <CardTitle className="text-base flex items-center justify-between gap-2">
                    <span className="flex items-center gap-1.5">
                        <FileEdit className="h-4 w-4" /> Work Order PDF
                    </span>
                    {hasTemplate && (
                        <Badge
                            variant="outline"
                            className={
                                confirmed
                                    ? "border-emerald-400/60 bg-emerald-50 text-emerald-700 dark:bg-emerald-950/40 dark:text-emerald-300"
                                    : "border-amber-400/60 bg-amber-50 text-amber-700 dark:bg-amber-950/40 dark:text-amber-300"
                            }
                        >
                            {confirmed ? "Mapping confirmed" : "Verify mapping"}
                        </Badge>
                    )}
                </CardTitle>
            </CardHeader>
            <CardContent className="space-y-2 text-sm">
                {hasTemplate ? (
                    <>
                        <div className="text-foreground/80">
                            <div className="truncate font-medium" title={wo.template_name ?? undefined}>
                                {wo.template_name ?? "Vendor template"}
                            </div>
                            <div className="text-xs text-foreground/60">
                                {fieldCount} field{fieldCount === 1 ? "" : "s"} mapped on the return PDF
                            </div>
                        </div>
                        {needsReview && (
                            <p className="text-xs text-amber-700 dark:text-amber-300">
                                {wo.auto_created
                                    ? "This job was created automatically — check where values stamp on the return PDF before the tech runs it."
                                    : "Check where values stamp on the return PDF before the tech runs it."}
                            </p>
                        )}
                    </>
                ) : (
                    <p className="text-xs text-amber-700 dark:text-amber-300">
                        No return-PDF template is linked to this work order yet — the vendor's completed form can't be
                        generated until one is set up from the work order below.
                    </p>
                )}
                <Button variant={needsReview ? "default" : "outline"} size="sm" className="w-full" asChild>
                    <Link to={`/jobs/work-orders/review/${wo.intake_item_id}`}>
                        <FileEdit className="h-3.5 w-3.5 mr-1.5" />
                        {hasTemplate ? "Review / fix PDF mapping" : "Open work order setup"}
                    </Link>
                </Button>
            </CardContent>
        </Card>
    );
}
